/**
 * is a React component tailored for pre-approved guests, providing a user-friendly interface to enter their 12-digit prescreen ID. This form enables guests to seamlessly progress through the pre-approval process, ensuring a smooth transition to the application page or the Credit Card Landing Page based on their interactions
 *
 * @module views/components/PreScreenIdInputForm
 * @memberof -Common
 */
import './PreScreenIdInputForm.scss';

import React, { useEffect, useRef, useState } from 'react';

import { Form } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import { FormikWithPersist } from '@ulta/core/components/FormikWithPersist/FormikWithPersist';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import InputField from '@ulta/core/components/InputField/InputField';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Spacer from '@ulta/core/components/Spacer/Spacer';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';

import {
  addYupCustomRegex,
  getIsButtonDisabled
} from '@ulta/utils/Validations/Validations';
import { createYupSchema } from '@ulta/utils/yupScehma/createYupSchema';

import * as utils from './PreScreenIdInputForm';
/**
 * Represents a PreScreenIdInputForm component
 *
 * @method
 * @param { PreScreenIdInputFormProps} props - React properties passed from composition
 * @returns PreScreenIdInputForm
 */
export const PreScreenIdInputForm = function( props ){
  const {
    componentKey,
    acceptSubTitle,
    acceptTitle,
    applySubTitle,
    applyAction,
    prescreenId,
    applyTitle,
    acceptAction,
    messages,
    invokeAction,
    id: contentId
  } = handleEmptyObjects( props );

  const formikRef = useRef( { values: {}, touched: {} } );
  const [showError, setShowError] = useState( hasItems( messages ) );
  const initialValues = {
    presceenIdNumber: ''
  };
  useEffect( () => {
    // Update showError when messages change
    setShowError( hasItems( messages ) );
  }, [messages] );
  if( !acceptTitle || !acceptSubTitle || !applyTitle || !applySubTitle ){
    return null;
  }

  const validationSchema = utils.getValidationSchema( { prescreenId } );

  return (
    <GridContainer>
      <div className='PreScreenIdInputForm__preScreenIdAccept'>
        <div className='PreScreenIdInputForm__headingContainer'>
          <Text htmlTag='h2'
            textStyle='title-6'
          >
            { acceptTitle }
          </Text>
          <Spacer value='01' />
          <Text htmlTag='p'
            textStyle='body-2'
            color='neutral-600'
          >
            { acceptSubTitle }
          </Text>
        </div>
        { showError &&
          messages?.map( ( data, i ) => (
            <BlockMessage
              key={ `${componentKey}-PreScreenIdInputForm-${i}` }
              message={ data.message }
              messageType={ data.icon }
              backgroundColor={ data.backgroundColor }
              borderColor={ data.borderColor }
              textColor={ data.textColor }
              dismissAction={ data.dismissAction }
              action1={ data.action1 }
              action1Style='link'
              action2={ data.action2 }
              showCloseButton={ true }
            />
          ) ) }
        <FormikWithPersist
          validationSchema={ validationSchema }
          initialValues={ initialValues }
          validateOnMount
          onSubmit={ ( values ) => {
            utils.handlePreScreenAcceptAction(
              {
                acceptAction,
                contentId,
                presceenIdNumber: values.presceenIdNumber
              },
              { invokeAction }
            );
          } }
          innerRef={ formikRef }
        >
          { ( { isValid, dirty } ) => (
            <Form>
              <InputField
                { ...spreadProps( { ...prescreenId } ) }
                onChange={ () => setShowError( false ) }
                autoComplete='prescreenId'
              />
              <Button
                type='submit'
                primary
                label={ acceptAction?.label }
                action={ acceptAction }
                fullWidth
                disabled={ getIsButtonDisabled( { isValid, dirty } ) }
                className='PreScreenIdInputForm__acceptBtn'
                withHover
              />
            </Form>
          ) }

        </FormikWithPersist>
      </div>
      <div className='PreScreenIdInputForm__preScreenIdApply'>
        <div className='PreScreenIdInputForm__headingContainer'>
          <Text htmlTag='h2'
            textStyle='title-6'
          >
            { applyTitle }
          </Text>
          <Spacer value='01' />
          <Text htmlTag='p'
            textStyle='body-2'
            color='neutral-600'
          >
            { applySubTitle }
          </Text>
        </div>
        { applyAction.label && (
          <div className='PreScreenIdInputForm__applyLink'>
            <Link_Huge
              iconImage={ 'ArrowForward' }
              iconsize={ 's' }
              iconRight={ true }
              action={ applyAction }
            >
              { applyAction.label }
            </Link_Huge>
          </div>
        ) }
      </div>
    </GridContainer>
  );
};

/**
 * Get validation schema for the Prescreen ID.
 * @param {Object} data - The data object of prescreenId.
 */
export const getValidationSchema = ( data ) => {
  const { prescreenId } = handleEmptyObjects( data );
  if( hasItems( prescreenId?.validations ) ){
    const field = {
      presceenIdNumber: {
        validations: prescreenId?.validations,
        required: true
      }
    };
    return createYupSchema( field );

  }
};

/**
 * Method to handle the actions for Accept Action
 * @method
 * @param {Object} data - Passing the graphql, headers and params as an argument.
 * @param {Object} methods - Passing invokeAction method as an argument.
 */
export const handlePreScreenAcceptAction = ( data, methods ) => {
  const { presceenIdNumber, contentId, acceptAction } = handleEmptyObjects( data );
  const { customHeaders, graphql, params, method } = handleEmptyObjects( acceptAction );
  const { invokeAction } = methods;
  if( !graphql ){
    return;
  }
  invokeAction( {
    graphql: graphql,
    customHeaders: customHeaders,
    params: params,
    method: method,
    variables: {
      contentId: contentId,
      moduleParams: {
        preScreenId: {
          value: presceenIdNumber
        }
      }
    }
  } );
};

/**
 * Property type definitions
 * @typedef PreScreenIdInputFormProps
 * @type {object}
 * @property {string} acceptSubTitle - The subtitle for the accept section.
 * @property {string} acceptTitle - The title for the accept section.
 * @property {object} acceptAction - The action object for Accept button.
 * @property {string} applySubTitle - The subtitle for the apply section.
 * @property {Object} applyAction - The action for applying.
 * @property {Object} prescreenId - The prescreen ID information.
 * @property {string} applyTitle - The title for the apply section.
 */
export const propTypes = {
  acceptSubTitle: PropTypes.string,
  acceptTitle: PropTypes.string,
  acceptAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  applySubTitle: PropTypes.string,
  applyAction: PropTypes.shape( {
    label: PropTypes.string,
    name: PropTypes.string
  } ),
  prescreenId: PropTypes.shape( {
    name: PropTypes.string,
    label: PropTypes.string
  } ),
  applyTitle: PropTypes.string
};

PreScreenIdInputForm.propTypes = propTypes;

export default PreScreenIdInputForm;
